// components/attendance_controls_bar.jsx
import React, { useMemo, useState, useEffect } from "react";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CalendarIcon,
  CalendarDaysIcon,
  TableCellsIcon,
  ArrowUturnRightIcon,
} from "@heroicons/react/24/outline";
import CalendarDaySelect from "./calendar_day_select";
import CalendarWeekSelect from "./calendar_week_select";
import CalendarMonthSelect from "./calendar_month_select";

// sposta la data in base alla vista corrente
function shiftDate(d, mode, dir = 1) {
  const nd = new Date(d);
  if (mode === "GIORNALIERO") nd.setDate(nd.getDate() + dir);
  if (mode === "SETTIMANALE") nd.setDate(nd.getDate() + 7 * dir);
  if (mode === "MENSILE") nd.setMonth(nd.getMonth() + dir);
  if (mode === "TRIMESTRALE") nd.setMonth(nd.getMonth() + dir);
  return nd;
}

// lun → dom a partire da una data
function getWeekFromDate(baseDate) {
  const d = new Date(baseDate);
  const dow = d.getDay(); // 0=dom
  const mondayDiff = dow === 0 ? -6 : 1 - dow;
  const monday = new Date(d);
  monday.setDate(d.getDate() + mondayDiff);
  const days = [monday];
  for (let i = 1; i < 7; i++) {
    const day = new Date(monday);
    day.setDate(monday.getDate() + i);
    days.push(day);
  }
  return days;
}

function getWeekRangeLabel(monday, sunday) {
  const startDay = monday.getDate();
  const endDay = sunday.getDate();
  const startMonth = monday.toLocaleDateString("it-IT", { month: "long" });
  const endMonth = sunday.toLocaleDateString("it-IT", { month: "long" });
  if (startMonth !== endMonth) return `${startDay} ${startMonth} - ${endDay} ${endMonth}`;
  return `${startDay} - ${endDay} ${startMonth}`;
}

// helpers “periodo corrente”
function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() &&
         a.getMonth() === b.getMonth() &&
         a.getDate() === b.getDate();
}
function isSameMonth(a, b) {
  return a.getFullYear() === b.getFullYear() &&
         a.getMonth() === b.getMonth();
}
function isSameWeek(a, b) {
  const [aMon] = getWeekFromDate(a);
  const [bMon] = getWeekFromDate(b);
  return isSameDay(aMon, bMon);
}

const ALL_TABS = [
  { key: "GIORNALIERO", label: "Giornaliero", Icon: CalendarIcon },
  { key: "SETTIMANALE", label: "Settimanale", Icon: CalendarDaysIcon },
  { key: "MENSILE", label: "Mensile", Icon: TableCellsIcon },
  { key: "TRIMESTRALE", label: "Trimestrale", Icon: TableCellsIcon },
];

export default function AttendanceControlsBar({
  className = "",
  mode,                 // "GIORNALIERO" | "SETTIMANALE" | "MENSILE" | "TRIMESTRALE"
  onModeChange,
  date,                 // Date (per TRIMESTRALE = primo mese del range)
  onDateChange,         // (Date) => void
  hideDailyTab = false,
  paddingClass = "pt-5",
  showQuarterlyTab = false, // opzionale, default false
}) {
  const [open, setOpen] = useState(false);
  const today = new Date();

  // derivati dalla data corrente
  const weekDays = useMemo(() => getWeekFromDate(date), [date]);
  const monday = weekDays[0];
  const sunday = weekDays[6];
  const monthDate = useMemo(() => new Date(date.getFullYear(), date.getMonth(), 1), [date]);

  // Tabs visibili
  const TABS = useMemo(() => {
    let tabs = ALL_TABS;
    if (hideDailyTab) {
      tabs = tabs.filter((t) => t.key !== "GIORNALIERO");
    }
    if (!showQuarterlyTab) {
      tabs = tabs.filter((t) => t.key !== "TRIMESTRALE");
    }
    return tabs;
  }, [hideDailyTab, showQuarterlyTab]);

  // Se “Giornaliero” è nascosto ma il mode corrente è giornaliero, passa a Settimanale
  useEffect(() => {
    if (hideDailyTab && mode === "GIORNALIERO") {
      onModeChange?.("SETTIMANALE");
    }
  }, [hideDailyTab, mode, onModeChange]);

  // etichetta del pannello selettore
  const pickerLabel =
    mode === "GIORNALIERO"
      ? "Visualizza Giorno"
      : mode === "SETTIMANALE"
      ? "Visualizza Settimana"
      : mode === "TRIMESTRALE"
      ? "Visualizza Trimestre"
      : "Visualizza Mese";

  // testo riassuntivo nel bottone
  const buttonSummary =
    mode === "GIORNALIERO"
      ? date.toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" })
      : mode === "SETTIMANALE"
      ? `${getWeekRangeLabel(monday, sunday)} / ${monday.getFullYear()}`
      : mode === "TRIMESTRALE"
      ? (() => {
          // date = primo mese del range (es. febbraio)
          const first = new Date(date.getFullYear(), date.getMonth(), 1);
          const third = new Date(first.getFullYear(), first.getMonth() + 2, 1);
          const m1 = first.toLocaleDateString("it-IT", { month: "long" });
          const m3 = third.toLocaleDateString("it-IT", { month: "long" });
          return `${m1} - ${m3} ${first.getFullYear()}`;
        })()
      : monthDate.toLocaleDateString("it-IT", { month: "long", year: "numeric" });

  // logica “periodo corrente”
  const isCurrentPeriod =
    mode === "GIORNALIERO" ? isSameDay(date, today) :
    mode === "SETTIMANALE" ? isSameWeek(date, today) :
    mode === "TRIMESTRALE" ? isSameMonth(date, new Date(today.getFullYear(), today.getMonth(), 1)) :
    isSameMonth(date, today);

  const resetLabel =
    mode === "GIORNALIERO" ? "Oggi" :
    mode === "SETTIMANALE" ? "Settimana corrente" :
    mode === "TRIMESTRALE" ? "Trimestre corrente" :
    "Mese corrente";

  const handleResetToCurrent = () => {
    if (mode === "GIORNALIERO") {onDateChange?.(new Date(today));}
    if (mode === "SETTIMANALE") {onDateChange?.(getWeekFromDate(today)[0]);} // lunedì di questa settimana
    if (mode === "MENSILE") {onDateChange?.(new Date(today.getFullYear(), today.getMonth(), 1));}
    if (mode === "TRIMESTRALE") {onDateChange?.(new Date(today.getFullYear(), today.getMonth(), 1));} // trimestre corrente: parte dal mese corrente
    setOpen(false);
  };

  return (
    <section className={className}>
      <div className={paddingClass}>
        <nav role="tablist" className="grid grid-cols-2 gap-2 sm:flex sm:gap-4">
          {TABS.map(({ key, label, Icon }) => {
            const active = mode === key;
            return (
              <button
                key={key}
                role="tab"
                aria-selected={active}
                onClick={() => {
                setOpen(false);
                  if (key === "SETTIMANALE") onDateChange?.(getWeekFromDate(date)[0]);
                  if (key === "MENSILE") onDateChange?.(new Date(date.getFullYear(), date.getMonth(), 1));

                  onModeChange?.(key);
                }}
                className={[
                  "h-11 sm:h-12 rounded-t-xl sm:rounded-t-xl text-xs sm:text-sm font-semibold",
                  "border border-gray-200 uppercase",
                  "w-auto sm:w-40 px-3 sm:px-4",
                  active
                    ? "bg-indigo-600 text-white shadow-md border-indigo-600"
                    : "bg-white text-gray-700 hover:bg-gray-50",
                  "inline-flex items-center justify-center gap-2",
                ].join(" ")}
                style={{ borderBottomColor: active ? "transparent" : undefined }}
              >
                <Icon aria-hidden="true" className={`h-5 w-5 ${active ? "text-white" : "text-indigo-600"}`} />
                {label}
              </button>
            );
          })}
        </nav>
      </div>
      <div className="border-b border-gray-200" />

      <div className="px-4 pt-3 pb-6 bg-gray-100 border-x border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-3 items-center gap-3">

          {/* Col 1: Picker label */}
          <div className="md:justify-self-start justify-self-stretch">
            <span className="truncate max-w-full md:max-w-[320px] uppercase font-medium block text-sm text-gray-800">
              {pickerLabel}
            </span>
          </div>

          {/* Col 2: Calendario centrato (frecce + bottone) */}
          <div className="flex items-center justify-center gap-2">
            <button
              onClick={() => onDateChange?.(shiftDate(date, mode, -1))}
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 shadow-sm"
              aria-label="Indietro"
            >
              <ChevronLeftIcon className="h-5 w-5" />
            </button>

            <button
              onClick={() => setOpen(!open)}
              className="
                group inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white
                px-3 py-2 text-sm font-semibold text-gray-800 shadow-sm hover:bg-gray-50
                focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400/60
                w-full sm:w-80 justify-between uppercase
              "
              aria-expanded={open}
              aria-label={pickerLabel}
            >
              <span className="truncate text-gray-600">
                {buttonSummary}
              </span>
              {open ? (
                <ChevronUpIcon className="h-4 w-4 rounded-md bg-indigo-100 p-0.5" />
              ) : (
                <ChevronDownIcon className="h-4 w-4 rounded-md bg-indigo-100 p-0.5" />
              )}
            </button>

            <button
              onClick={() => onDateChange?.(shiftDate(date, mode, +1))}
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 shadow-sm"
              aria-label="Avanti"
            >
              <ChevronRightIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Col 3: azione “torna al periodo corrente” */}
          <div className="md:justify-self-end justify-self-stretch flex justify-end">
            {!isCurrentPeriod && (
              <button
                onClick={handleResetToCurrent}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-indigo-600 hover:bg-indigo-50 shadow-sm uppercase"
                aria-label={resetLabel}
                title={resetLabel}
              > 
                <ArrowUturnRightIcon className="h-4 w-4" />
                {resetLabel}
              </button>
            )}
          </div>

        </div>
      </div>

      {/* PANEL del calendario — custom per tab */}
      {open && (
        <div className="flex justify-center">
          <div className="w-full max-w-full">
            {mode === "GIORNALIERO" && (
              <CalendarDaySelect
                selectedMonth={date}
                onDaySelect={(d) => {
                  onDateChange?.(new Date(d));
                  setOpen(false);
                }}
                onCloseCalendar={() => setOpen(false)}
              />
            )}

            {mode === "SETTIMANALE" && (
              <div
                style={{ flex: "1 1 auto", width: "auto" }}
                className="py-2 px-4 border-t border-l border-r border-gray-200 text-center w-24 text-xs font-semibold text-gray-600 uppercase align-middle"
              >
                <CalendarWeekSelect
                  selectedMonth={monthDate}
                  onWeekSelect={(weekDaysArr) => {
                    const newMonday = new Date(weekDaysArr[0]);
                    onDateChange?.(newMonday);
                    setOpen(false);
                  }}
                  onCloseCalendar={() => setOpen(false)}
                />
              </div>
            )}

            {mode === "MENSILE" && (
              <div
                style={{ flex: "1 1 auto", width: "auto" }}
                className="pb-5 pt-2 px-4 border-x border-gray-200 text-center w-24 text-xs font-semibold text-gray-600 uppercase align-middle"
              >
                <CalendarMonthSelect
                  selectedMonth={monthDate}
                  onMonthSelect={(newMonthDate) => {
                    const first = new Date(
                      newMonthDate.getFullYear(),
                      newMonthDate.getMonth(),
                      1
                    );
                    onDateChange?.(first);
                    setOpen(false);
                  }}
                  onCloseCalendar={() => setOpen(false)}
                />
              </div>
            )}

            {mode === "TRIMESTRALE" && (
              <div
                style={{ flex: "1 1 auto", width: "auto" }}
                className="pb-5 pt-2 px-4 border-x border-gray-200 text-center w-24 text-xs font-semibold text-gray-600 uppercase align-middle"
              >
                <CalendarMonthSelect
                  selectedMonth={monthDate}
                  onMonthSelect={(newMonthDate) => {
                    const first = new Date(
                      newMonthDate.getFullYear(),
                      newMonthDate.getMonth(),
                      1
                    );
                    onDateChange?.(first);
                    setOpen(false);
                  }}
                  onCloseCalendar={() => setOpen(false)}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}